/**
 * ExtraChargeDialog — modal opened from the cart action bar to add a
 * single extra charge (Transportation / Labour / Installation / custom).
 *
 * Lives separately from CartTotals so the cart isn't visually cluttered
 * by an always-on composer when no extra charges are needed.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Truck } from 'lucide-react';

const PRESET_LABELS = ['Transportation', 'Labour', 'Installation', 'Carriage', 'Service Charge'];

export function ExtraChargeDialog({ open, onOpenChange, onAdd }) {
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');

  // Reset fields every time the dialog opens so it always starts blank.
  useEffect(() => {
    if (open) {
      setLabel('');
      setAmount('');
    }
  }, [open]);

  const handleSubmit = () => {
    const finalLabel = label.trim();
    const finalAmount = parseFloat(amount) || 0;
    if (!finalLabel) return;
    onAdd({ label: finalLabel, amount: finalAmount });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="extra-charge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1F4E79]">
            <Truck className="w-5 h-5" />
            Add Extra Charge
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="ec-label" className="text-xs text-[#5C6150]">Charge Name</Label>
            <Input
              id="ec-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Transportation, Labour, Installation"
              className="bg-white border-[#E2E6D9]"
              data-testid="extra-charge-label-input"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && label.trim()) handleSubmit(); }}
            />
            <div className="flex flex-wrap gap-1 pt-1">
              {PRESET_LABELS.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setLabel(p)}
                  className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                    label === p
                      ? 'bg-[#1F4E79] text-white border-[#1F4E79]'
                      : 'border-[#C9D5E1] text-[#1F4E79] hover:bg-[#1F4E79]/10'
                  }`}
                  data-testid={`preset-${p.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ec-amount" className="text-xs text-[#5C6150]">Amount (₹)</Label>
            <Input
              id="ec-amount"
              type="number"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              className="bg-white border-[#E2E6D9] font-mono-data"
              data-testid="extra-charge-amount-input"
              onKeyDown={(e) => { if (e.key === 'Enter' && label.trim()) handleSubmit(); }}
            />
          </div>
        </div>

        <DialogFooter className="flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
            data-testid="extra-charge-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!label.trim()}
            className="flex-1 bg-[#1F4E79] hover:bg-[#163A5C] text-white"
            data-testid="extra-charge-submit-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add to Quotation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ExtraChargeDialog;
